"use client";

import { useAnalytics } from "@analytics";
import { LocaleLink, useLocalePathname } from "@i18n/routing";
import {
	MobileNavMenu,
	type NavMenuGroup,
	type NavMenuItem,
	type NavMenuSection,
} from "@marketing/shared/components/MobileNavMenu";
import { workflowHubs } from "@marketing/shared/lib/workflow-hubs";
import { MARKETING_EVENTS } from "@repo/analytics";
import type { PricingClickedData } from "@repo/analytics";
import { config } from "@repo/config";
import { useSession } from "@saas/auth/hooks/use-session";
import { ColorModeToggle } from "@shared/components/ColorModeToggle";
import { LocaleSwitch } from "@shared/components/LocaleSwitch";
import { Logo } from "@shared/components/Logo";
import { Button } from "@ui/components/button";
import {
	Sheet,
	SheetContent,
	SheetTitle,
	SheetTrigger,
} from "@ui/components/sheet";
import { cn } from "@ui/lib";
import {
	ActivityIcon,
	ArrowRightIcon,
	BarChart3Icon,
	BookOpenIcon,
	BotIcon,
	CalculatorIcon,
	CalendarClockIcon,
	ChevronDownIcon,
	CrosshairIcon,
	FolderCheckIcon,
	GaugeIcon,
	GlobeIcon,
	HelpCircleIcon,
	LayersIcon,
	ListChecksIcon,
	ListPlusIcon,
	MapPinIcon,
	MegaphoneIcon,
	MenuIcon,
	NetworkIcon,
	NewspaperIcon,
	PackageIcon,
	RadioIcon,
	SearchIcon,
	ShieldCheckIcon,
	SparklesIcon,
	UserIcon,
	WrenchIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import NextLink from "next/link";
import { Suspense, useEffect, useRef, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";

/**
 * Three or more headed sections render side by side (Products' Track /
 * Score / Optimize / For PR teams, Resources' three groups); mixed or
 * unheaded sections keep stacking under their trigger.
 */
function isColumnar(sections: NavMenuSection[]) {
	return sections.length >= 3 && sections.every((s) => s.heading);
}

function MegaDropdownItem({
	item,
	columnar,
	featured,
	onClose,
}: {
	item: NavMenuItem;
	columnar: boolean;
	featured?: boolean;
	onClose: () => void;
}) {
	return (
		<LocaleLink
			href={item.href}
			onClick={() => {
				item.onClick?.();
				onClose();
			}}
			className={cn(
				"group flex items-start gap-3 rounded-lg px-3 transition-colors",
				featured ? "hover:bg-background" : "hover:bg-muted",
				// A six-item column at the stacked padding is ~590px, which
				// does not fit a small laptop viewport under the nav.
				columnar ? "py-2" : "py-2.5",
			)}
		>
			<div
				className={cn(
					"mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors",
					featured
						? "bg-background shadow-xs"
						: "bg-muted group-hover:bg-background",
				)}
			>
				{item.icon}
			</div>
			<div className="min-w-0">
				<div className="font-medium text-foreground text-sm">
					{item.label}
				</div>
				{item.description ? (
					<div
						className={cn(
							"mt-0.5 text-muted-foreground text-xs leading-snug",
							// On short viewports a three-line blurb is what
							// pushes the tallest column past the panel cap;
							// clamp to two lines there.
							columnar &&
								"line-clamp-3 [@media(max-height:720px)]:line-clamp-2",
						)}
					>
						{item.description}
					</div>
				) : null}
			</div>
		</LocaleLink>
	);
}

function MegaDropdownHeading({
	children,
	featured,
}: {
	children: React.ReactNode;
	featured?: boolean;
}) {
	return (
		<div
			className={cn(
				"px-3 pb-1 font-semibold text-[11px] uppercase tracking-wider",
				// The featured column's p-1 already supplies 4px of the gap.
				featured ? "pt-1 text-primary" : "pt-2 text-muted-foreground",
			)}
		>
			{children}
		</div>
	);
}

function MegaDropdown({
	sections,
	open,
	onClose,
}: {
	sections: NavMenuSection[];
	open: boolean;
	onClose: () => void;
}) {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) {
			return;
		}
		const handler = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				onClose();
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [open, onClose]);

	if (!open) {
		return null;
	}

	const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);
	const isWide = totalItems > 3;
	const columnar = isColumnar(sections);

	return (
		<div
			ref={ref}
			className={cn(
				// Cap the panel at the viewport and scroll inside it, so a short
				// laptop screen never scrolls the page under an open menu.
				"absolute top-full left-1/2 z-[100] max-h-[calc(100vh-6rem)] -translate-x-1/2 overflow-y-auto rounded-xl border bg-popover p-2 text-left shadow-lg",
				columnar
					? // Centered on the nav row (the positioned ancestor);
						// -mt-3 tucks it up into the row's bottom padding.
						"-mt-3 flex w-max gap-1"
					: cn("mt-2", isWide ? "w-[560px]" : "w-[320px]"),
			)}
		>
			{sections.map((section, si) =>
				columnar && section.featured ? (
					<div
						key={section.heading ?? si}
						className="ml-1 flex w-56 flex-col rounded-lg bg-primary/5 p-1 ring-1 ring-primary/15 ring-inset xl:w-60"
					>
						<MegaDropdownHeading featured>
							{section.heading}
						</MegaDropdownHeading>
						{section.blurb ? (
							<p className="px-3 pb-1 text-muted-foreground text-xs leading-snug">
								{section.blurb}
							</p>
						) : null}
						<div className="flex flex-col gap-0.5">
							{section.items.map((item) => (
								<MegaDropdownItem
									key={item.href}
									item={item}
									columnar
									featured
									onClose={onClose}
								/>
							))}
						</div>
						{section.footerLink ? (
							<LocaleLink
								href={section.footerLink.href}
								onClick={onClose}
								className="mx-2 mt-1 flex items-center gap-1.5 border-primary/15 border-t px-1 pt-2.5 pb-1.5 font-medium text-primary text-sm hover:underline"
							>
								{section.footerLink.label}
								<ArrowRightIcon
									aria-hidden
									className="size-3.5"
								/>
							</LocaleLink>
						) : null}
					</div>
				) : (
					<div
						key={section.heading ?? si}
						className={columnar ? "w-56 xl:w-64" : undefined}
					>
						{section.heading ? (
							<MegaDropdownHeading>
								{section.heading}
							</MegaDropdownHeading>
						) : null}
						<div
							className={
								isWide && !section.heading && !columnar
									? "grid grid-cols-2 gap-0.5"
									: "flex flex-col gap-0.5"
							}
						>
							{section.items.map((item) => (
								<MegaDropdownItem
									key={item.href}
									item={item}
									columnar={columnar}
									onClose={onClose}
								/>
							))}
						</div>
						{!columnar && si < sections.length - 1 && (
							<div className="my-1 border-t" />
						)}
					</div>
				),
			)}
		</div>
	);
}

export function NavBar() {
	const t = useTranslations();
	const { user } = useSession();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const localePathname = useLocalePathname();
	const [isTop, setIsTop] = useState(true);
	const { trackEvent } = useAnalytics();
	const [openDropdown, setOpenDropdown] = useState<string | null>(null);

	const debouncedScrollHandler = useDebounceCallback(
		() => {
			setIsTop(window.scrollY <= 10);
		},
		150,
		{
			maxWait: 150,
		},
	);

	useEffect(() => {
		window.addEventListener("scroll", debouncedScrollHandler);
		debouncedScrollHandler();
		return () => {
			window.removeEventListener("scroll", debouncedScrollHandler);
		};
	}, [debouncedScrollHandler]);

	useEffect(() => {
		setMobileMenuOpen(false);
		setOpenDropdown(null);
	}, [localePathname]);

	const isDocsPage = localePathname.startsWith("/docs");

	const handlePricingClick = () => {
		trackEvent(MARKETING_EVENTS.PRICING_CLICKED, {
			source: "navbar",
			location: localePathname,
		} as PricingClickedData);
	};

	// Products are grouped by the brand narrative: Track (know where you
	// stand), Score (know what media is worth), Optimize (move the numbers).
	// New pages join their group here as they ship.
	const productSections: NavMenuSection[] = [
		{
			heading: t("common.menu.nav.groups.track"),
			items: [
				{
					label: t("common.menu.products.aiVisibilityReport"),
					href: "/ai-visibility-report",
					description: t(
						"common.menu.nav.descriptions.aiVisibilityReport",
					),
					icon: <BarChart3Icon className="size-4 text-blue-600" />,
				},
				{
					label: t("common.menu.products.promptTracking"),
					href: "/prompt-tracking",
					description: t(
						"common.menu.nav.descriptions.promptTracking",
					),
					icon: (
						<CalendarClockIcon className="size-4 text-cyan-600" />
					),
				},
				{
					label: t("common.menu.products.personTracking"),
					href: "/person-tracking",
					description: t(
						"common.menu.nav.descriptions.personTracking",
					),
					icon: <UserIcon className="size-4 text-emerald-600" />,
				},
				{
					label: t("common.menu.products.locationTracking"),
					href: "/location-tracking",
					description: t(
						"common.menu.nav.descriptions.locationTracking",
					),
					icon: <MapPinIcon className="size-4 text-orange-600" />,
				},
				{
					label: t("common.menu.products.productTracking"),
					href: "/product-tracking",
					description: t(
						"common.menu.nav.descriptions.productTracking",
					),
					icon: <PackageIcon className="size-4 text-lime-600" />,
				},
				{
					label: t("common.menu.products.categoryTracking"),
					href: "/category-tracking",
					description: t(
						"common.menu.nav.descriptions.categoryTracking",
					),
					icon: <LayersIcon className="size-4 text-amber-600" />,
				},
				{
					label: t("common.menu.products.aiTrafficAnalytics"),
					href: "/ai-traffic-analytics",
					description: t(
						"common.menu.nav.descriptions.aiTrafficAnalytics",
					),
					icon: <ActivityIcon className="size-4 text-violet-600" />,
				},
			],
		},
		{
			heading: t("common.menu.nav.groups.score"),
			items: [
				{
					label: t("common.menu.products.citationIntelligence"),
					href: "/citation-intelligence",
					description: t(
						"common.menu.nav.descriptions.citationIntelligence",
					),
					icon: <NetworkIcon className="size-4 text-sky-600" />,
				},
				{
					label: t("common.menu.products.aiPlacementValueScore"),
					href: "/ai-placement-value-score",
					description: t(
						"common.menu.nav.descriptions.aiPlacementValueScore",
					),
					icon: <GaugeIcon className="size-4 text-teal-600" />,
				},
				{
					label: t("common.menu.products.aiPlacementQualityScore"),
					href: "/ai-placement-quality-score",
					description: t(
						"common.menu.nav.descriptions.aiPlacementQualityScore",
					),
					icon: <CrosshairIcon className="size-4 text-violet-600" />,
				},
			],
		},
		{
			heading: t("common.menu.nav.groups.optimize"),
			items: [
				{
					label: t("common.menu.products.citationOptimizer"),
					href: "/citation-optimizer",
					description: t(
						"common.menu.nav.descriptions.citationOptimizer",
					),
					icon: <SparklesIcon className="size-4 text-fuchsia-600" />,
				},
				{
					label: t("common.menu.products.aiReadinessAudit"),
					href: "/ai-readiness-audit",
					description: t(
						"common.menu.nav.descriptions.aiReadinessAudit",
					),
					icon: (
						<ShieldCheckIcon className="size-4 text-emerald-600" />
					),
				},
				{
					label: t("common.menu.products.faqGeneration"),
					href: "/faq-generation",
					description: t(
						"common.menu.nav.descriptions.faqGeneration",
					),
					icon: <HelpCircleIcon className="size-4 text-amber-600" />,
				},
				{
					label: t("common.menu.products.mcpServer"),
					href: "/mcp-server",
					description: t("common.menu.nav.descriptions.mcpServer"),
					icon: <GlobeIcon className="size-4 text-indigo-600" />,
				},
			],
		},
		{
			// A featured column for PR buyers: their two tools, and a way into
			// the full workflow on /for-pr-firms.
			heading: t("common.menu.nav.groups.prTeams"),
			featured: true,
			blurb: t("common.menu.nav.prTeams.blurb"),
			items: [
				{
					label: t("common.menu.products.mediaLists"),
					href: "/media-list",
					description: t("common.menu.nav.descriptions.mediaLists"),
					icon: <ListPlusIcon className="size-4 text-rose-600" />,
				},
				{
					label: t("common.menu.products.coverageReports"),
					href: "/coverage-report",
					description: t(
						"common.menu.nav.descriptions.coverageReports",
					),
					icon: (
						<FolderCheckIcon className="size-4 text-purple-600" />
					),
				},
			],
			footerLink: {
				label: t("common.menu.nav.prTeams.footerLink"),
				href: "/for-pr-firms",
			},
		},
	];

	const resourceSections: NavMenuSection[] = [
		{
			heading: t("common.menu.nav.groups.freeTools"),
			items: [
				{
					label: t("common.menu.nav.freeTools.promptGenerator.label"),
					href: "/free-ai-visibility-prompt-generator",
					description: t(
						"common.menu.nav.freeTools.promptGenerator.description",
					),
					icon: <SparklesIcon className="size-4 text-purple-600" />,
				},
				{
					label: t(
						"common.menu.nav.freeTools.personalAiVisibilityReport.label",
					),
					href: "/personal-ai-visibility-report",
					description: t(
						"common.menu.nav.freeTools.personalAiVisibilityReport.description",
					),
					icon: <UserIcon className="size-4 text-emerald-600" />,
				},
				{
					label: t("common.menu.nav.freeTools.aiAccessChecker.label"),
					href: "/ai-access-checker",
					description: t(
						"common.menu.nav.freeTools.aiAccessChecker.description",
					),
					icon: <BotIcon className="size-4 text-teal-600" />,
				},
				{
					label: t(
						"common.menu.nav.freeTools.commonCrawlRankChecker.label",
					),
					href: "/common-crawl-rank-checker",
					description: t(
						"common.menu.nav.freeTools.commonCrawlRankChecker.description",
					),
					icon: <NetworkIcon className="size-4 text-indigo-600" />,
				},
				{
					label: t(
						"common.menu.nav.freeTools.reliabilityCalculator.label",
					),
					href: "/is-your-ai-visibility-data-reliable",
					description: t(
						"common.menu.nav.freeTools.reliabilityCalculator.description",
					),
					icon: <CalculatorIcon className="size-4 text-orange-600" />,
				},
				{
					label: t("common.menu.nav.freeTools.aiAnalyticsLive.label"),
					href: "/ai-analytics-live",
					description: t(
						"common.menu.nav.freeTools.aiAnalyticsLive.description",
					),
					icon: <RadioIcon className="size-4 text-red-500" />,
				},
			],
		},
		{
			heading: t("common.menu.nav.groups.guides"),
			items: [
				{
					label: t("common.menu.nav.guides.agencies.label"),
					href: workflowHubs.agencies.path,
					description: t(
						"common.menu.nav.guides.agencies.description",
					),
					icon: <ListChecksIcon className="size-4 text-amber-600" />,
				},
				{
					label: t("common.menu.nav.guides.pr.label"),
					href: workflowHubs.pr.path,
					description: t("common.menu.nav.guides.pr.description"),
					icon: <ListChecksIcon className="size-4 text-amber-600" />,
				},
				{
					label: t("common.menu.nav.guides.inHouse.label"),
					href: workflowHubs.inHouse.path,
					description: t(
						"common.menu.nav.guides.inHouse.description",
					),
					icon: <ListChecksIcon className="size-4 text-amber-600" />,
				},
				{
					label: t("common.menu.nav.guides.content.label"),
					href: workflowHubs.content.path,
					description: t(
						"common.menu.nav.guides.content.description",
					),
					icon: <ListChecksIcon className="size-4 text-amber-600" />,
				},
			],
		},
		{
			// Headed so Resources lays out in three columns (Free Tools, Guides,
			// Explore) like Products does. Stacked, the twelve items overflow a
			// short laptop viewport.
			heading: t("common.menu.nav.groups.explore"),
			items: [
				{
					label: t("common.menu.bots"),
					href: "/bots",
					description: t("common.menu.nav.descriptions.bots"),
					icon: <NewspaperIcon className="size-4 text-sky-600" />,
				},
				{
					label: t("common.menu.blog"),
					href: "/blog",
					description: t("common.menu.nav.descriptions.blog"),
					icon: <BookOpenIcon className="size-4 text-rose-600" />,
				},
			],
		},
	];

	const solutionSections: NavMenuSection[] = [
		{
			items: [
				{
					label: t("common.menu.nav.solutionsItems.forAgencies"),
					href: "/for-agencies",
					description: t("common.menu.nav.descriptions.forAgencies"),
					icon: <SearchIcon className="size-4 text-indigo-600" />,
				},
				{
					label: t("common.menu.nav.solutionsItems.forPRFirms"),
					href: "/for-pr-firms",
					description: t("common.menu.nav.descriptions.forPRFirms"),
					icon: <MegaphoneIcon className="size-4 text-pink-600" />,
				},
				{
					label: t("common.menu.nav.solutionsItems.services"),
					href: "/services",
					description: t("common.menu.nav.descriptions.services"),
					icon: <WrenchIcon className="size-4 text-orange-600" />,
				},
			],
		},
	];

	const dropdowns: NavMenuGroup[] = [
		{
			key: "products",
			label: t("common.menu.products.title"),
			sections: productSections,
		},
		{
			key: "solutions",
			label: t("common.menu.nav.solutions"),
			sections: solutionSections,
		},
		{
			key: "resources",
			label: t("common.menu.nav.resources"),
			sections: resourceSections,
		},
	];

	const standaloneItems: NavMenuItem[] = [
		{
			label: t("common.menu.pricing"),
			href: "/pricing",
			onClick: handlePricingClick,
		},
		{
			label: t("common.menu.docs"),
			href: "/docs",
		},
	];

	const isMenuItemActive = (href: string) => localePathname.startsWith(href);

	const isDropdownActive = (sections: NavMenuSection[]) =>
		sections.some((s) =>
			s.items.some((item) => isMenuItemActive(item.href)),
		);

	return (
		<nav
			className={cn(
				"fixed top-0 left-0 z-50 w-full transition-shadow duration-200",
				!isTop || isDocsPage
					? "bg-card/80 shadow-sm backdrop-blur-lg"
					: "shadow-none",
			)}
			data-test="navigation"
		>
			<div className="container">
				<div
					className={cn(
						// Positioned so the wide columnar panels center on the
						// whole row rather than on their trigger.
						"relative flex items-center justify-stretch gap-4 transition-[padding] duration-200 xl:gap-6",
						!isTop || isDocsPage ? "py-4" : "py-6",
					)}
				>
					<div className="flex flex-1 justify-start">
						<LocaleLink
							href="/"
							className="block hover:no-underline active:no-underline"
						>
							<Logo />
						</LocaleLink>
					</div>

					<div className="hidden flex-2 items-center justify-center lg:flex">
						{dropdowns.map((dd) => (
							<div
								key={dd.key}
								// Narrow panels hang under their trigger; columnar
								// ones position against the nav row instead.
								className={
									isColumnar(dd.sections)
										? undefined
										: "relative"
								}
							>
								<button
									type="button"
									className={cn(
										"flex items-center gap-1 px-2 py-2 text-base text-foreground/80 hover:text-foreground transition-colors xl:px-3 xl:text-lg",
										isDropdownActive(dd.sections)
											? "font-bold text-foreground"
											: "",
									)}
									onClick={() =>
										setOpenDropdown(
											openDropdown === dd.key
												? null
												: dd.key,
										)
									}
									onMouseEnter={() => setOpenDropdown(dd.key)}
								>
									{dd.label}
									<ChevronDownIcon
										className={cn(
											"size-4 transition-transform",
											openDropdown === dd.key
												? "rotate-180"
												: "",
										)}
									/>
								</button>
								<MegaDropdown
									sections={dd.sections}
									open={openDropdown === dd.key}
									onClose={() => setOpenDropdown(null)}
								/>
							</div>
						))}

						{standaloneItems.map((item) => (
							<LocaleLink
								key={item.href}
								href={item.href}
								onClick={item.onClick}
								className={cn(
									"block px-2 py-2 text-base text-foreground/80 xl:px-3 xl:text-lg",
									isMenuItemActive(item.href)
										? "font-bold text-foreground"
										: "",
								)}
							>
								{item.label}
							</LocaleLink>
						))}
					</div>

					<div className="flex flex-1 items-center justify-end gap-2 xl:gap-3">
						<ColorModeToggle />
						{config.i18n.enabled && (
							<Suspense>
								<LocaleSwitch />
							</Suspense>
						)}

						<Sheet
							open={mobileMenuOpen}
							onOpenChange={(open) => setMobileMenuOpen(open)}
						>
							<SheetTrigger asChild>
								<Button
									className="lg:hidden"
									size="icon"
									variant="light"
									aria-label="Menu"
								>
									<MenuIcon className="size-4" />
								</Button>
							</SheetTrigger>
							<SheetContent
								// The menu owns its scrolling and pins the account
								// buttons, so the sheet is a bare full-height column.
								// sheetVariants appends className without merging, so
								// its p-6, gap-4 and h-full would otherwise win the
								// cascade; `!` makes these overrides stick.
								className="flex h-dvh! w-[300px] flex-col gap-0! p-0!"
								side="right"
								// No description: the title and the links say it all.
								aria-describedby={undefined}
							>
								<MobileNavMenu
									groups={dropdowns}
									standaloneItems={standaloneItems}
									isMenuItemActive={isMenuItemActive}
									signedIn={!!user}
									labels={{
										login: t("common.menu.login"),
										signup: t("common.menu.signup"),
										dashboard: t("common.menu.dashboard"),
									}}
									linkComponent={LocaleLink}
									authLinkComponent={NextLink}
									onNavigate={() => setMobileMenuOpen(false)}
									header={
										<>
											<SheetTitle className="sr-only">
												Menu
											</SheetTitle>
											<LocaleLink
												href="/"
												onClick={() =>
													setMobileMenuOpen(false)
												}
												className="block [&_svg]:h-8 [&_svg]:w-auto"
											>
												<Logo />
											</LocaleLink>
										</>
									}
								/>
							</SheetContent>
						</Sheet>

						{config.ui.saas.enabled &&
							(user ? (
								<Button
									key="dashboard"
									className="hidden lg:flex"
									asChild
									variant="secondary"
								>
									<NextLink href="/app">
										{t("common.menu.dashboard")}
									</NextLink>
								</Button>
							) : (
								<>
									<Button
										key="login"
										className="hidden whitespace-nowrap lg:flex"
										asChild
										variant="ghost"
									>
										<NextLink href="/auth/login">
											{t("common.menu.login")}
										</NextLink>
									</Button>
									<Button
										key="signup"
										className="hidden whitespace-nowrap lg:flex"
										asChild
										variant="secondary"
									>
										<NextLink href="/auth/signup">
											{/* Below xl a long label (DE) pushes the
											    row past the viewport; the short one
											    fits. EN uses the same text for both. */}
											<span className="xl:hidden">
												{t("common.menu.signupShort")}
											</span>
											<span className="hidden xl:inline">
												{t("common.menu.signup")}
											</span>
										</NextLink>
									</Button>
								</>
							))}
					</div>
				</div>
			</div>
		</nav>
	);
}
